import { LoadingButton } from '@mui/lab';
import { Box, TextField } from '@mui/material';
import { unstable_useId as useId } from '@mui/material/utils';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateDevice } from '~/api/devices';
import { Tooltip } from '~/components/Tooltip';
import { Label } from '~/components/striped-card/styles';
import { StripedContainer } from '~/components/striped-container';

export interface DeviceNotesProps {
  deviceId: number;
  notes: string | null | undefined;
}

export const DeviceNotes = ({ deviceId, notes }: DeviceNotesProps) => {
  const { t } = useTranslation();
  const [updateDevice, { loading }] = useUpdateDevice();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [dirty, setDirty] = useState(false);
  const labelId = useId();

  const save = async () => {
    const result = await updateDevice({
      variables: { deviceId, patch: { notes: ref.current?.value ?? '' } },
    });

    // The mutation resolves even on failure, so clearing unconditionally would hide Save with
    // the edit still unsaved.
    if (result.data?.updateDevice?.device) setDirty(false);
  };

  return (
    <StripedContainer>
      <Label id={labelId}>{t('pages.devices.show.settings.fields.notes.label')}</Label>
      <TextField
        defaultValue={notes ?? ''}
        disabled={loading}
        fullWidth
        // The card's heading names the field; the Label is not a label element.
        inputProps={{ 'aria-labelledby': labelId, maxLength: 5000 }}
        inputRef={ref}
        multiline
        // Scrolls past six rows rather than growing the card off the page.
        maxRows={6}
        minRows={3}
        onChange={(event) => setDirty(event.target.value !== (notes ?? ''))}
        onKeyDown={(event) => {
          // Uncontrolled, so abandoning the draft means writing the original back to the node.
          if (event.key === 'Escape') {
            if (ref.current) ref.current.value = notes ?? '';
            setDirty(false);
            return;
          }

          // Enter stays a newline, as it must for prose; the modifier saves. Cmd and Ctrl both,
          // so it works on a Mac and everywhere else.
          if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return;
          event.preventDefault();
          if (dirty) void save();
        }}
        variant="standard"
        // The card already draws a box; an input border inside it reads as a second one.
        InputProps={{ disableUnderline: true }}
      />
      {dirty && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title={t('pages.devices.show.additional.notes_save_hint')}>
            <LoadingButton
              loading={loading}
              onClick={() => void save()}
              size="small"
              variant="contained"
            >
              {t('common.actions.save')}
            </LoadingButton>
          </Tooltip>
        </Box>
      )}
    </StripedContainer>
  );
};
